// src/components/cards/UniversalOrderCard.jsx - VERSÃO 6.0 SUPREMA (FINANCE + VIP)
import React from 'react';
import { 
    FiHash, FiFileText, FiUser, FiPhone, FiLayers, FiZap, FiStar, FiDollarSign 
} from 'react-icons/fi';
import { motion } from 'framer-motion';

export default function UniversalOrderCard({ order, actions }) {
    // 🛰️ DNA: Extração dos últimos 5 dígitos do protocolo
    const shortProtocol = order.protocolo ? order.protocolo.slice(-5) : '00000';
    
    // 🧮 CARGA: Soma total de linhas (chips)
    const totalLines = order.itens?.reduce((acc, it) => acc + (it.quantidade || 0), 0) || 0;

    // 💰 FINANCEIRO: Cálculo do Valor Total Mensal (Qtd x Preço)
    const totalMonthly = order.itens?.reduce((acc, it) => {
        const preco = it.precoUnitario || 0;
        return acc + (it.quantidade * preco);
    }, 0) || 0;
    
    // 📦 ARMAMENTO: Identificação do Plano
    const mainPlan = order.itens?.[0]?.titulo || 'Nenhum plano selecionado';

    // 🎖️ IDENTIFICAÇÃO VIP: Verifica carimbo no banco (order ou empresa)
    const isVIP = order.isVIP || order.empresa?.isVIP || totalLines > 20; 

    return (
        <motion.div 
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`group p-8 rounded-[2.5rem] border bg-card-auto shadow-2xl relative overflow-hidden transition-all duration-500 hover:-translate-y-2 ${isVIP ? 'border-accent/40 shadow-accent/10' : 'border-auto hover:border-accent'}`}
        >
            {/* 🛰️ HEADER HUD */}
            <div className="flex justify-between items-start mb-6">
                <div className="space-y-2">
                    <div className="flex items-center gap-2 opacity-30">
                        <FiHash size={12} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Protocolo</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <h3 className="text-4xl font-black text-accent tracking-tighter italic leading-none">
                            {shortProtocol}
                        </h3>
                        {isVIP && (
                            <div className="flex items-center gap-1 bg-accent/20 text-accent px-3 py-1 rounded-full border border-accent/30 animate-pulse">
                                <FiStar size={10} className="fill-accent" />
                                <span className="text-[8px] font-black uppercase tracking-widest">CLIENTE VIP</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center bg-main-auto border-auto shadow-inner transition-all ${isVIP ? 'text-accent border-accent/50' : 'text-accent group-hover:animate-pulse'}`}>
                    <FiZap size={20} />
                </div>
            </div>

            {/* 📋 DOSSIÊ CORPORATIVO */}
            <div className="space-y-4 mb-8 py-6 border-y border-auto">
                <div className="grid grid-cols-1 gap-4">
                    <DataRow icon={FiFileText} label="CNPJ Identificado" value={order.empresa?.cnpj} />
                    <DataRow icon={FiLayers} label="Razão Social" value={order.empresa?.razaoSocial} bold />
                    <DataRow icon={FiUser} label="Solicitante Master" value={order.solicitante?.nome} />
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-auto/10">
                    <DataRow icon={FiPhone} label="Contato Principal" value={order.solicitante?.contato1} />
                    <DataRow icon={FiPhone} label="Contato Auxiliar" value={order.solicitante?.contato2 || '---'} />
                </div>
            </div>

            {/* ⚔️ ESPECIFICAÇÕES TÁTICAS (LEITURA VERTICAL) */}
            <div className="p-5 rounded-2xl bg-main-auto border border-auto shadow-inner mb-8 space-y-5">
                {/* Linhas e Valor na mesma linha para equilíbrio visual */}
                <div className="flex justify-between items-end">
                    <div className="flex flex-col">
                        <span className="text-[7px] font-black uppercase opacity-30 tracking-[0.3em] mb-1">Total de Linhas</span>
                        <span className="text-2xl font-black text-auto leading-none">
                            {totalLines} <span className="text-[10px] opacity-40 uppercase font-bold text-accent">Chips</span>
                        </span>
                    </div>
                    <div className="flex flex-col text-right">
                        <span className="text-[7px] font-black uppercase opacity-30 tracking-[0.3em] mb-1">Receita Mensal (MRR)</span>
                        <span className="text-xl font-black text-green-500 leading-none">
                            {totalMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                    </div>
                </div>
                
                <div className="pt-4 border-t border-auto/10 flex flex-col">
                    <span className="text-[7px] font-black uppercase opacity-30 tracking-[0.3em] mb-2">Plano Escolhido no Arsenal</span>
                    <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-accent/10 border border-accent/20">
                            <FiZap size={14} className="text-accent" />
                        </div>
                        <span className="text-[12px] font-black text-auto uppercase leading-tight italic">
                            {mainPlan}
                        </span>
                    </div>
                </div>
            </div>

            {/* 🛠️ AÇÕES DE COMANDO */}
            {actions && (
                <div className="mt-auto relative z-10">
                    {actions}
                </div>
            )}

            {/* Overlay de Fundo para VIP */}
            {isVIP && (
                <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 blur-[60px] rounded-full -mr-16 -mt-16 pointer-events-none" />
            )}

            <div className="absolute bottom-0 left-10 right-10 h-1 bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-700 rounded-t-full" />
        </motion.div>
    );
}

const DataRow = ({ icon: Icon, label, value, bold }) => (
    <div className="flex items-start gap-3 group/row">
        <div className="w-6 h-6 flex items-center justify-center">
            <Icon size={14} className="text-accent opacity-40 group-hover/row:opacity-100 transition-opacity" />
        </div>
        <div className="flex flex-col min-w-0">
            <span className="text-[7px] font-black uppercase opacity-20 tracking-widest leading-none mb-1">{label}</span>
            <span className={`text-[11px] uppercase truncate ${bold ? 'font-black text-auto' : 'font-bold opacity-70'}`}>
                {value || '---'}
            </span>
        </div>
    </div>
);